import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/common/PasswordInput';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  Sparkle,
  Heartbeat,
  CaretRight,
  Baby,
  User,
  Drop,
  FirstAid,
  ChartBar,
  Package,
  ListChecks,
  ClipboardText,
  Truck,
  Camera,
  Gear,
  Lock,
  WarningCircle,
  Buildings,
  GraduationCap,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const ModuleSelector = ({ onBack, onSelectModule, userName, user }) => {
  const { language } = useLanguage();
  const { token } = useAuth();
  const isAdminOrManager = !!(user?.is_admin || user?.role === 'admin' || user?.role === 'manager');
  const isManagerOnly = !!(user?.role === 'manager' && !user?.is_admin);
  const isAdmin = !!(user?.is_admin || user?.role === 'admin');
  // Accès B2B : admin, manager (Cyrille), supervisor (Bret), accountant (comptable).
  const canAccessB2B = !!(
    user?.is_admin ||
    user?.role === 'admin' ||
    user?.role === 'manager' ||
    user?.role === 'supervisor' ||
    user?.role === 'accountant'
  );

  const [adminCodeOpen, setAdminCodeOpen] = useState(false);
  const [adminCode, setAdminCode] = useState('');
  const [adminCodeError, setAdminCodeError] = useState('');
  const [verifying, setVerifying] = useState(false);

  const verifyAdminCode = async (e) => {
    e?.preventDefault?.();
    if (!adminCode.trim()) {
      setAdminCodeError(language === 'fr' ? 'Veuillez saisir le code' : 'Please enter the code');
      return;
    }
    setVerifying(true);
    setAdminCodeError('');
    try {
      await axios.post(
        `${API_URL}/settings/sadadmin-code/verify`,
        { code: adminCode.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAdminCodeOpen(false);
      setAdminCode('');
      onSelectModule('sadadmin');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        setAdminCodeError(language === 'fr' ? 'Code incorrect' : 'Incorrect code');
      } else if (status === 403) {
        setAdminCodeError(
          language === 'fr' ? 'Accès réservé aux administrateurs' : 'Admin access required',
        );
      } else {
        setAdminCodeError(language === 'fr' ? 'Erreur de vérification' : 'Verification error');
        toast.error(language === 'fr' ? 'Erreur réseau' : 'Network error');
      }
    } finally {
      setVerifying(false);
    }
  };

  // ----- Business module cards (4 apps shown in a 2x2 grid) ----------------
  const businessCards = [
    {
      id: 'skincare',
      title: 'SADASkinCare',
      tag: language === 'fr' ? 'Dermo-Cosmétique' : 'Dermo-Cosmetics',
      description:
        language === 'fr'
          ? 'Diagnostic et conseil dermo-cosmétique'
          : 'Skin diagnosis and dermo-cosmetic advice',
      onClick: () => onSelectModule('skincare'),
      testId: 'select-skincare-module',
      tagBg: '#2E7D32',
      iconBg: 'bg-[#E8F5E9]',
      iconColor: 'text-[#2E7D32]',
      hover: 'group-hover:text-[#2E7D32]',
      arrowHover: 'group-hover:bg-[#2E7D32]',
      Icon: Sparkle,
      image: '/images/sadaskincare_mirror.png',
      pills: [
        { Icon: User, label: 'Adulte', cls: 'bg-[#E8F5E9] text-[#2E7D32]' },
        { Icon: Baby, label: 'Bébé', cls: 'bg-[#FDF2F3] text-[#C08B8F]' },
        { Icon: Drop, label: 'Soins', cls: 'bg-blue-50 text-blue-600' },
      ],
    },
    {
      id: 'health',
      title: 'SADAHealth',
      tag: language === 'fr' ? 'Suivi Santé' : 'Health Tracking',
      description:
        language === 'fr'
          ? 'Suivi santé et paramètres vitaux'
          : 'Health monitoring and vital signs',
      onClick: () => onSelectModule('health'),
      testId: 'select-health-module',
      tagBg: '#2563EB',
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
      hover: 'group-hover:text-blue-600',
      arrowHover: 'group-hover:bg-blue-600',
      Icon: Heartbeat,
      image: '/images/sada_health_ta.png',
      pills: [
        { Icon: Heartbeat, label: 'Tension', cls: 'bg-blue-50 text-blue-600' },
        { Icon: FirstAid, label: 'Glycémie', cls: 'bg-amber-50 text-amber-600' },
        { Icon: ChartBar, label: 'IMC', cls: 'bg-emerald-50 text-emerald-600' },
      ],
    },
    {
      id: 'inventory',
      title: 'SADARayons',
      tag: language === 'fr' ? 'Inventaire & Rayons' : 'Inventory & Shelves',
      description:
        language === 'fr'
          ? 'Inventaire tournant & pilotage des écarts'
          : 'Rolling inventory & discrepancy management',
      onClick: () => onSelectModule('inventory'),
      testId: 'select-inventory-module',
      tagBg: '#5B3B8C',
      iconBg: 'bg-purple-50',
      iconColor: 'text-[#5B3B8C]',
      hover: 'group-hover:text-[#5B3B8C]',
      arrowHover: 'group-hover:bg-[#5B3B8C]',
      Icon: Package,
      gradient: 'bg-gradient-to-br from-[#5B3B8C] via-[#7C4FB8] to-[#9B6FD3]',
      pills: [
        { Icon: ListChecks, label: 'Cycles', cls: 'bg-purple-50 text-[#5B3B8C]' },
        { Icon: ClipboardText, label: 'Écarts', cls: 'bg-violet-50 text-violet-600' },
        { Icon: ChartBar, label: 'KPI', cls: 'bg-fuchsia-50 text-fuchsia-600' },
      ],
    },
    {
      id: 'reception',
      title: 'SADAGAS',
      tag: language === 'fr' ? 'Réception IA' : 'AI Reception',
      description:
        language === 'fr'
          ? 'Gestion des Achats & Stock — réception IA des livraisons'
          : 'Procurement — AI-powered delivery reception',
      onClick: () => onSelectModule('reception'),
      testId: 'select-reception-module',
      tagBg: '#0F766E',
      iconBg: 'bg-teal-50',
      iconColor: 'text-[#0F766E]',
      hover: 'group-hover:text-[#0F766E]',
      arrowHover: 'group-hover:bg-[#0F766E]',
      Icon: Truck,
      gradient: 'bg-gradient-to-br from-[#0F766E] via-[#0D9488] to-[#14B8A6]',
      pills: [
        { Icon: Camera, label: 'Photos', cls: 'bg-teal-50 text-[#0F766E]' },
        { Icon: Sparkle, label: 'IA', cls: 'bg-cyan-50 text-cyan-700' },
        { Icon: ListChecks, label: 'BL', cls: 'bg-emerald-50 text-emerald-700' },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col font-body">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="hover:bg-gray-100 rounded-xl"
            data-testid="module-selector-back"
          >
            <ArrowLeft size={22} weight="bold" className="text-gray-600" />
          </Button>
          <div>
            <h1 className="font-display text-lg font-semibold text-gray-900">
              {language === 'fr' ? 'Bonjour' : 'Hello'}, {userName}
            </h1>
            <p className="text-sm text-gray-500">
              {language === 'fr' ? 'Choisissez un module' : 'Choose a module'}
            </p>
          </div>
        </div>
      </header>

      {/* Module Cards */}
      <div className="flex-1 flex items-start justify-center p-6">
        <motion.div
          className="max-w-5xl w-full"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.h2
            className="text-center font-display text-2xl font-bold text-gray-900 mb-8"
            variants={fadeInUp}
          >
            {language === 'fr' ? 'Que souhaitez-vous faire ?' : 'What would you like to do?'}
          </motion.h2>

          {/* 2x2 grid for the 4 business apps */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {businessCards.map((c) => {
              const Icon = c.Icon;
              return (
                <motion.div key={c.id} variants={fadeInUp} className="h-full">
                  <Card
                    className="cursor-pointer border border-gray-100 bg-white shadow-card rounded-2xl overflow-hidden group hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 h-full"
                    onClick={c.onClick}
                    data-testid={c.testId}
                  >
                    {/* Image / gradient header */}
                    <div className={`h-[160px] w-full overflow-hidden relative ${c.gradient || ''}`}>
                      {c.image ? (
                        <img
                          src={c.image}
                          alt={c.title}
                          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <>
                          <div
                            className="absolute inset-0 opacity-20"
                            style={{
                              backgroundImage:
                                'radial-gradient(circle at 25% 25%, rgba(255,255,255,0.4) 0, transparent 50%), radial-gradient(circle at 75% 75%, rgba(255,255,255,0.3) 0, transparent 50%)',
                            }}
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Icon
                              size={80}
                              weight="duotone"
                              className="text-white/90 drop-shadow-lg group-hover:scale-110 transition-transform duration-500"
                            />
                          </div>
                        </>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                      <div className="absolute bottom-3 left-3">
                        <span
                          className="text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-md"
                          style={{ backgroundColor: c.tagBg }}
                        >
                          {c.tag}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-5">
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-11 h-11 ${c.iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}
                        >
                          <Icon size={22} weight="duotone" className={c.iconColor} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3
                            className={`font-display text-lg font-bold text-gray-900 mb-1 transition-colors ${c.hover}`}
                          >
                            {c.title}
                          </h3>
                          <p className="text-gray-500 text-sm mb-3 leading-relaxed">
                            {c.description}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {c.pills.map((p, i) => {
                              const PIcon = p.Icon;
                              return (
                                <span
                                  key={i}
                                  className={`inline-flex items-center gap-1.5 text-xs ${p.cls} px-2.5 py-1 rounded-full font-medium`}
                                >
                                  <PIcon size={12} weight="bold" />
                                  {p.label}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                        <div
                          className={`w-9 h-9 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${c.arrowHover}`}
                        >
                          <CaretRight
                            size={16}
                            weight="bold"
                            className="text-gray-400 group-hover:text-white transition-colors"
                          />
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* SADAdmin banner — admin only. Cyrille (manager) has a dedicated
              "Espace Manager · Saisie" banner below with direct access (no code). */}
          {isAdmin && (
            <motion.div variants={fadeInUp} className="max-w-4xl mx-auto mt-6">
              <Card
                className="cursor-pointer border border-gray-200 bg-gradient-to-br from-[#1F2937] via-[#374151] to-[#4B5563] shadow-card rounded-2xl overflow-hidden group hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-300"
                onClick={() => {
                  setAdminCode('');
                  setAdminCodeError('');
                  setAdminCodeOpen(true);
                }}
                data-testid="select-sadadmin-banner"
              >
                <div className="p-5 sm:p-6 flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/15 backdrop-blur rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Gear size={28} weight="duotone" className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-display text-xl font-bold text-white">SADAdmin</h3>
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-white/20 text-white px-2 py-0.5 rounded-full">
                        <Lock size={10} weight="bold" />
                        {language === 'fr' ? 'Code requis' : 'Code required'}
                      </span>
                    </div>
                    <p className="text-white/80 text-sm">
                      {language === 'fr'
                        ? 'Pilotage transverse — SkinCare · Health · Rayons · GAS · Système · Client · RH'
                        : 'Cross-module administration'}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-white/15 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-white group-hover:text-[#1F2937] transition-colors">
                    <CaretRight size={18} weight="bold" className="text-white group-hover:text-[#1F2937] transition-colors" />
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Espace Manager — Cyrille uniquement (manager non admin) */}
          {isManagerOnly && (
            <motion.div variants={fadeInUp} className="max-w-4xl mx-auto mt-6">
              <Card
                className="cursor-pointer border border-amber-200 bg-gradient-to-br from-[#D97706] via-[#F59E0B] to-[#FBBF24] shadow-card rounded-2xl overflow-hidden group hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-300"
                onClick={() => onSelectModule('sadadmin')}
                data-testid="select-manager-banner"
              >
                <div className="p-5 sm:p-6 flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Gear size={28} weight="duotone" className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-display text-xl font-bold text-white">
                        {language === 'fr' ? 'Espace Manager' : 'Manager Space'}
                      </h3>
                    </div>
                    <p className="text-white/90 text-sm">
                      {language === 'fr'
                        ? 'Saisie manager · suivi équipe · SADAStock inventaire'
                        : 'Manager input · team follow-up · SADAStock inventory'}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-white group-hover:text-[#D97706] transition-colors">
                    <CaretRight size={18} weight="bold" className="text-white group-hover:text-[#D97706] transition-colors" />
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {/* B2B Cliniques — module métier autonome, parallèle aux autres modules.
              Visible pour admin / manager (Cyrille) / supervisor (Bret) / accountant (compta).
              Pas de gateway code : l'utilisateur authentifié + autorisé y accède directement. */}
          {canAccessB2B && (
            <motion.div variants={fadeInUp} className="max-w-4xl mx-auto mt-4">
              <Card
                className="cursor-pointer border border-violet-200 bg-gradient-to-br from-[#7C3AED] via-[#8B5CF6] to-[#A78BFA] shadow-card rounded-2xl overflow-hidden group hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-300"
                onClick={() => onSelectModule('b2b')}
                data-testid="select-b2b-banner"
              >
                <div className="p-5 sm:p-6 flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Buildings size={28} weight="duotone" className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-display text-xl font-bold text-white">SADA B2B</h3>
                    </div>
                    <p className="text-white/90 text-sm">
                      {language === 'fr'
                        ? 'Demandes cliniques · cotations · exécution · suivi paiement'
                        : 'Clinic orders · quotes · fulfillment · payment tracking'}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-white group-hover:text-[#7C3AED] transition-colors">
                    <CaretRight size={18} weight="bold" className="text-white group-hover:text-[#7C3AED] transition-colors" />
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {/* SADA Academy — Learning Management accessible à TOUS les utilisateurs
              connectés (agents, auxiliaires, pharmaciens, admins). Chacun voit son
              propre catalogue de formations et sa progression. */}
          <motion.div variants={fadeInUp} className="max-w-4xl mx-auto mt-4">
            <Card
              className="cursor-pointer border border-emerald-200 bg-gradient-to-br from-[#059669] via-[#10B981] to-[#34D399] shadow-card rounded-2xl overflow-hidden group hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-300"
              onClick={() => onSelectModule('academy')}
              data-testid="select-academy-banner"
            >
              <div className="p-5 sm:p-6 flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center flex-shrink-0">
                  <GraduationCap size={28} weight="duotone" className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-display text-xl font-bold text-white">🎓 SADA Academy</h3>
                  </div>
                  <p className="text-white/90 text-sm">
                    {language === 'fr'
                      ? 'Formations HP LIFE · YALI · Microsoft Learn · HPCI · Mes certificats & progression'
                      : 'HP LIFE · YALI · Microsoft Learn · HPCI trainings · My certificates & progress'}
                  </p>
                </div>
                <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-white group-hover:text-[#059669] transition-colors">
                  <CaretRight size={18} weight="bold" className="text-white group-hover:text-[#059669] transition-colors" />
                </div>
              </div>
            </Card>
          </motion.div>
        </motion.div>
      </div>

      {/* SADAdmin Access Code Dialog */}
      <Dialog open={adminCodeOpen} onOpenChange={setAdminCodeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock size={20} weight="duotone" className="text-[#1F2937]" />
              {language === 'fr' ? "Code d'accès SADAdmin" : 'SADAdmin Access Code'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={verifyAdminCode} className="space-y-4 py-2">
            <p className="text-sm text-gray-500">
              {language === 'fr'
                ? "Saisissez le code d'accès pour ouvrir le pilotage."
                : 'Enter the access code to open admin.'}
            </p>
            <PasswordInput
              autoFocus
              placeholder={language === 'fr' ? "Code d'accès" : 'Access code'}
              value={adminCode}
              onChange={(e) => setAdminCode(e.target.value)}
              className="text-center text-lg tracking-[0.25em] font-medium py-5"
              data-testid="sadadmin-code-input"
            />
            {adminCodeError && (
              <div className="flex items-center justify-center gap-2 text-red-600 text-sm">
                <WarningCircle size={16} weight="fill" />
                {adminCodeError}
              </div>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setAdminCodeOpen(false)}
                data-testid="sadadmin-code-cancel"
              >
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button
                type="submit"
                disabled={verifying}
                className="bg-[#1F2937] hover:bg-[#111827] text-white"
                data-testid="sadadmin-code-submit"
              >
                {verifying
                  ? language === 'fr'
                    ? 'Vérification…'
                    : 'Verifying…'
                  : language === 'fr'
                  ? 'Accéder'
                  : 'Open'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModuleSelector;
